import createGridComponent from './createGridComponent';
import { Defaults } from './internal';

// each cell of a row may have metadata about column span

// each row will get a cell span metadata map

const getEstimatedTotalHeight = ({
  rowCount,
  itemData
}, {
  rowMetadataMap,
  estimatedRowHeight,
  lastMeasuredRowIndex
}) => {
  if (itemData.columns.length === 0) return 0;
  let totalSizeOfMeasuredRows = 0; // Edge case check for when the number of items decreases while a scroll is in progress.
  // https://github.com/bvaughn/react-window/pull/138

  if (lastMeasuredRowIndex >= rowCount) {
    lastMeasuredRowIndex = rowCount - 1;
  }
  if (lastMeasuredRowIndex >= 0) {
    const itemMetadata = rowMetadataMap[lastMeasuredRowIndex];
    totalSizeOfMeasuredRows = itemMetadata.offset + itemMetadata.size;
  }
  const numUnmeasuredItems = rowCount - lastMeasuredRowIndex - 1;
  const totalSizeOfUnmeasuredItems = numUnmeasuredItems * estimatedRowHeight;
  return totalSizeOfMeasuredRows + totalSizeOfUnmeasuredItems;
};
const getColumnWidth = (_props, index, instanceProps) => {
  return instanceProps.columnMetadataMap[index]?.size;
};
const setColumnWidth = (index, props, instanceProps, width) => {
  if (!instanceProps.columnMetadataMap[index + props.columnCount - 1]) {
    getItemMetadata('column', props, index + props.columnCount - 1, instanceProps);
  }
  // set the width of this column to the given width
  instanceProps.columnMetadataMap[index].size = width;

  // adjust the offset for each subsequent columnn
  let offset = instanceProps.columnMetadataMap[index].offset;
  for (let i = index; i <= props.columnCount - 1; i++) {
    const size = instanceProps.columnMetadataMap[i].size;
    instanceProps.columnMetadataMap[i].offset = offset;
    offset += size;
  }
};
const getEstimatedTotalWidth = ({
  columnCount
}, {
  columnMetadataMap,
  estimatedColumnWidth,
  lastMeasuredColumnIndex
}) => {
  if (columnCount === 0) return 0;
  let totalSizeOfMeasuredRows = 0; // Edge case check for when the number of items decreases while a scroll is in progress.
  // https://github.com/bvaughn/react-window/pull/138

  if (lastMeasuredColumnIndex >= columnCount) {
    lastMeasuredColumnIndex = columnCount - 1;
  }
  if (lastMeasuredColumnIndex >= 0) {
    const itemMetadata = columnMetadataMap[lastMeasuredColumnIndex];
    totalSizeOfMeasuredRows = itemMetadata.offset + itemMetadata.size;
  }
  const numUnmeasuredItems = columnCount - lastMeasuredColumnIndex - 1;
  const totalSizeOfUnmeasuredItems = numUnmeasuredItems * estimatedColumnWidth;
  return totalSizeOfMeasuredRows + totalSizeOfUnmeasuredItems;
};
const getFrozenColumnWidth = (props, instanceProps) => {
  if (!props.frozenColumnCount || props.frozenColumnCount === 0) return 0;
  return getItemMetadata('column', props, props.frozenColumnCount, instanceProps).offset;
};
const getItemMetadata = (itemType, props, index, instanceProps, shouldSetMetadata) => {
  let itemMetadataMap, itemSize, lastMeasuredIndex;
  if (itemType === 'column') {
    itemMetadataMap = instanceProps.columnMetadataMap;
    itemSize = props.columnWidth;
    lastMeasuredIndex = instanceProps.lastMeasuredColumnIndex;
  } else if (itemType === 'row') {
    itemMetadataMap = instanceProps.rowMetadataMap;
    itemSize = props.rowHeight;
    lastMeasuredIndex = instanceProps.lastMeasuredRowIndex;
  } else {
    // assume column header row now
    itemMetadataMap = instanceProps.headerRowMetadataMap;
    lastMeasuredIndex = instanceProps.lastMeasuredHeaderRowIndex;
    index = 0 - index; // row headers start at -1 and progress negatively, but cached as 0..+n
    // itemSize will be:
    //  1. props.headerRowHeight function if provided
    //  2. function that returns props.headerHeight if provided
    //  3. instanceProps.estimatedRowHeight
    if (typeof props.headerRowHeight === 'function') {
      itemSize = props.headerRowHeight;
    } else if (props.headerHeight) {
      itemSize = _x => props.headerHeight ?? instanceProps.headerHeight;
    } else {
      itemSize = _x => instanceProps.estimatedRowHeight;
    }
  }
  if (index > lastMeasuredIndex && shouldSetMetadata !== false) {
    let offset = 0;
    if (lastMeasuredIndex >= 0) {
      const itemMetadata = itemMetadataMap[lastMeasuredIndex];
      offset = itemMetadata.offset + itemMetadata.size;
    }
    for (let i = lastMeasuredIndex + 1; i <= index; i++) {
      const size = itemSize(i);
      itemMetadataMap[i] = {
        offset,
        size
      };
      offset += size;
    }
    if (itemType === 'column') {
      instanceProps.lastMeasuredColumnIndex = index;
    } else if (itemType === 'row' && index < props.rowCount) {
      instanceProps.lastMeasuredRowIndex = index;
    } else if (itemType === 'columnHeaderRow') {
      instanceProps.lastMeasuredHeaderRowIndex = index;
    }
  }
  return itemMetadataMap[index];
};
const getAdjustedColumnWidth = (colWidth, index, offset, props) => {
  if (index === props.columnCount - 1 && props.autoFillLastColumn !== false) {
    return Math.max(props.itemData.getViewPortWidth() - offset, colWidth);
  }
  return colWidth;
};
const findNearestItem = (itemType, props, instanceProps, offset) => {
  let itemMetadataMap, lastMeasuredIndex;
  if (itemType === 'column') {
    itemMetadataMap = instanceProps.columnMetadataMap;
    lastMeasuredIndex = instanceProps.lastMeasuredColumnIndex;
  } else if (itemType === 'row') {
    itemMetadataMap = instanceProps.rowMetadataMap;
    lastMeasuredIndex = instanceProps.lastMeasuredRowIndex;
  } else {
    itemMetadataMap = instanceProps.headerRowMetadataMap;
    lastMeasuredIndex = instanceProps.lastMeasuredHeaderRowIndex;
  }
  const lastMeasuredItemOffset = lastMeasuredIndex > 0 ? itemMetadataMap[lastMeasuredIndex].offset : 0;
  if (lastMeasuredItemOffset >= offset) {
    // If we've already measured items within this range just use a binary search as it's faster.
    return findNearestItemBinarySearch(itemType, props, instanceProps, lastMeasuredIndex, 0, offset);
  } else {
    // If we haven't yet measured this high, fallback to an exponential search with an inner binary search.
    // The exponential search avoids pre-computing sizes for the full set of items as a binary search would.
    // The overall complexity for this approach is O(log n).
    return findNearestItemExponentialSearch(itemType, props, instanceProps, Math.max(0, lastMeasuredIndex), offset);
  }
};
const findNearestItemBinarySearch = (itemType, props, instanceProps, high, low, offset) => {
  while (low <= high) {
    const middle = low + Math.floor((high - low) / 2);
    const currentOffset = getItemMetadata(itemType, props, middle, instanceProps).offset;
    if (currentOffset === offset) {
      return middle;
    } else if (currentOffset < offset) {
      low = middle + 1;
    } else if (currentOffset > offset) {
      high = middle - 1;
    }
  }
  if (low > 0) {
    return low - 1;
  } else {
    return 0;
  }
};
const findNearestItemExponentialSearch = (itemType, props, instanceProps, index, offset) => {
  const itemCount = itemType === 'column' ? props.columnCount : props.rowCount;
  let interval = 1;
  while (index < itemCount && getItemMetadata(itemType, props, index, instanceProps).offset < offset) {
    index += interval;
    interval *= 2;
  }
  return findNearestItemBinarySearch(itemType, props, instanceProps, Math.min(index, itemCount - 1), Math.floor(index / 2), offset);
};
const getOffsetForIndexAndAlignment = (itemType, props, index, align, scrollOffset, instanceProps, scrollbarSize) => {
  const size = itemType === 'column' ? props.width : props.height - (instanceProps.headerHeight ?? 0);
  const itemMetadata = getItemMetadata(itemType, props, index, instanceProps);
  const frozenOffset = itemType === 'column' ? getFrozenColumnWidth(props, instanceProps) : 0;
  const maxOffset = Math.max(0, itemMetadata.offset - frozenOffset);
  const minOffset = Math.max(0, itemMetadata.offset - size + scrollbarSize + itemMetadata.size);
  if (align === 'smart') {
    if (scrollOffset >= minOffset - size && scrollOffset <= maxOffset + size) {
      align = 'auto';
    } else if (size > 0) {
      align = 'center';
    }
  }
  switch (align) {
    case 'start':
      return maxOffset;
    case 'end':
      return minOffset;
    case 'center':
      return Math.round(minOffset + (maxOffset - minOffset) / 2);
    case 'auto':
    default:
      if (scrollOffset >= minOffset && scrollOffset <= maxOffset) {
        return scrollOffset;
      } else if (minOffset > maxOffset) {
        // This happens when the cell width is larger than scrollable size (i.e. tableSize - frozenOffset)
        // Always start justify the cell
        return maxOffset;
      } else if (scrollOffset < minOffset) {
        return minOffset;
      } else {
        return maxOffset;
      }
  }
};
export const VariableSizeGrid = createGridComponent({
  getColumnOffset: (props, index, instanceProps) => getItemMetadata('column', props, index, instanceProps).offset,
  getColumnStartIndexForOffset: (props, scrollLeft, instanceProps) => findNearestItem('column', props, instanceProps, scrollLeft),
  getColumnStopIndexForStartIndex: (props, startIndex, scrollLeft, instanceProps, shouldSetMetadata) => {
    const {
      columnCount,
      width
    } = props;
    const itemMetadata = getItemMetadata('column', props, startIndex, instanceProps, shouldSetMetadata);
    const maxOffset = scrollLeft + width;
    let offset = itemMetadata.offset + itemMetadata.size;
    let stopIndex = startIndex;
    while (stopIndex < columnCount - 1 && offset < maxOffset) {
      stopIndex++;
      offset += getItemMetadata('column', props, stopIndex, instanceProps, shouldSetMetadata).size;
    }
    return stopIndex;
  },
  getColumnWidth,
  setColumnWidth,
  getAdjustedColumnWidth,
  getEstimatedTotalHeight,
  getEstimatedTotalWidth,
  getOffsetForColumnAndAlignment: (props, index, align, scrollOffset, instanceProps, scrollbarSize) => getOffsetForIndexAndAlignment('column', props, index, align, scrollOffset, instanceProps, scrollbarSize),
  getOffsetForRowAndAlignment: (props, index, align, scrollOffset, instanceProps, scrollbarSize) => getOffsetForIndexAndAlignment('row', props, index, align, scrollOffset, instanceProps, scrollbarSize),
  getRowOffset: (props, index, instanceProps) => getItemMetadata('row', props, index, instanceProps).offset,
  getRowHeight: (props, index, instanceProps) => {
    if (index < 0) {
      return getItemMetadata('columnHeaderRow', props, 0 - index - 1, instanceProps).size;
    }
    return getItemMetadata('row', props, index, instanceProps).size;
  },
  getRowStartIndexForOffset: (props, scrollTop, instanceProps) => findNearestItem('row', props, instanceProps, scrollTop),
  getRowStopIndexForStartIndex: (props, startIndex, scrollTop, instanceProps) => {
    const {
      rowCount,
      height
    } = props;
    const headerVisible = props.headerVisible ?? true;
    // The column header is considered as the first row, so the total
    // number of rows is rowCount + 1 when the column header is visible
    const maxIndex = headerVisible ? rowCount : rowCount - 1;
    const itemMetadata = getItemMetadata('row', props, startIndex, instanceProps);
    const maxOffset = scrollTop + height;
    let offset = itemMetadata.offset + itemMetadata.size;
    let stopIndex = startIndex;
    while (stopIndex < maxIndex && offset < maxOffset) {
      stopIndex++;
      offset += getItemMetadata('row', props, stopIndex, instanceProps).size;
    }

    // Return the actual data row index by subtracting the column header row if visible
    return headerVisible ? stopIndex - 1 : stopIndex;
  },
  getRowMetadata: (instanceProps, index, isHeader) => {
    const rowIndex = index; // due to parameter name conflicts

    const mapOfMaps = isHeader === true ? instanceProps.spanHeaderRowMetadataMap : instanceProps.spanRowMetadataMap;
    if (mapOfMaps[index] === undefined) {
      // create and save the col span map for the requested row
      mapOfMaps[index] = {
        lastIndex: -1,
        lastReset: -1
      };
    }
    const row = mapOfMaps[index];
    if (row.lastReset !== mapOfMaps.lastColReset) {
      row.lastIndex = Math.min(row.lastIndex, mapOfMaps.lastColIndex);
      const lastMeta = row[row.lastIndex];
      if (lastMeta !== undefined && lastMeta.displayed < row.lastIndex) {
        row.lastIndex = lastMeta.displayed;
      }
      row.lastReset = mapOfMaps.lastColReset;
    }
    const setCell = (index, value) => {
      row[index] = value;
      row.lastIndex = Math.max(index + (value?.colSpan ?? 1), row.lastIndex);
      // copy the value to each cell of the map covered by this span (>1)
      for (let i = index + 1, l = index + value.colSpan; i < l; i++) {
        row[i] = value;
      }
    };
    return {
      getCell: (index, getColumnSpan) => {
        if (index >= row.lastIndex) {
          let i = Math.max(row.lastIndex, 0);
          while (i <= index) {
            // find the span of the column at 'i'
            const span = typeof getColumnSpan === 'function' ? getColumnSpan(rowIndex, i) : getColumnSpan;

            // fill in that metadata
            setCell(i, {
              displayed: i,
              colSpan: span
            });

            // move forward by 'span' columns
            i += span;
          }
        }
        return row[index];
      },
      setCell
    };
  },
  initInstanceProps(props, instance) {
    const {
      estimatedColumnWidth,
      estimatedRowHeight
    } = props;
    const instanceProps = {
      columnMetadataMap: {},
      estimatedColumnWidth: estimatedColumnWidth || Defaults.sizes.columnWidth,
      estimatedRowHeight: estimatedRowHeight || Defaults.sizes.rowHeight,
      lastMeasuredColumnIndex: -1,
      lastMeasuredRowIndex: -1,
      lastMeasuredHeaderRowIndex: -1,
      rowMetadataMap: {},
      headerRowMetadataMap: {},
      headerHeight: estimatedRowHeight || Defaults.sizes.rowHeight,
      spanRowMetadataMap: {
        lastRowIndex: -1,
        lastColIndex: -1,
        lastColReset: -1
      },
      spanHeaderRowMetadataMap: {
        lastRowIndex: -1,
        lastColIndex: -1,
        lastColReset: -1
      }
    };
    instance.resetAfterColumnIndex = (columnIndex, shouldForceUpdate = true) => {
      instance.resetAfterIndices({
        columnIndex,
        shouldForceUpdate
      });
    };
    instance.resetAfterRowIndex = (rowIndex, shouldForceUpdate = true) => {
      instance.resetAfterIndices({
        rowIndex,
        shouldForceUpdate
      });
    };
    instance.resetAfterIndices = ({
      columnIndex,
      rowIndex,
      shouldForceUpdate = true
    }) => {
      if (typeof columnIndex === 'number') {
        instanceProps.lastMeasuredColumnIndex = Math.min(instanceProps.lastMeasuredColumnIndex, columnIndex - 1);

        // reset the last reliable column index for our colspan caches
        instanceProps.spanRowMetadataMap.lastColIndex = instanceProps.spanHeaderRowMetadataMap.lastColIndex = Math.min(columnIndex - 1, instanceProps.spanRowMetadataMap.lastColIndex);
        instanceProps.spanRowMetadataMap.lastColReset = instanceProps.spanHeaderRowMetadataMap.lastColReset = Date.now();
      }
      if (typeof rowIndex === 'number') {
        instanceProps.lastMeasuredRowIndex = Math.min(instanceProps.lastMeasuredRowIndex, rowIndex - 1);

        // reset the last reliable row index for our colspan cache
        instanceProps.spanRowMetadataMap.lastRowIndex = Math.min(rowIndex - 1, instanceProps.spanRowMetadataMap.lastRowIndex);
      } // We could potentially optimize further by only evicting styles after this index,
      // But since styles are only cached while scrolling is in progress-
      // It seems an unnecessary optimization.
      // It's unlikely that resetAfterIndex() will be called while a user is scrolling.

      instance._getItemStyleCache(-1);
      if (shouldForceUpdate) {
        instance.forceUpdate();
      }
    };
    return instanceProps;
  },
  shouldResetStyleCacheOnItemSizeChange: false,
  validateProps: ({
    columnWidth,
    rowHeight
  }) => {
    if (process.env.NODE_ENV !== 'production') {
      if (typeof columnWidth !== 'function') {
        throw Error('An invalid "columnWidth" prop has been specified. ' + 'Value should be a function. ' + `"${columnWidth === null ? 'null' : typeof columnWidth}" was specified.`);
      } else if (typeof rowHeight !== 'function') {
        throw Error('An invalid "rowHeight" prop has been specified. ' + 'Value should be a function. ' + `"${rowHeight === null ? 'null' : typeof rowHeight}" was specified.`);
      }
    }
  }
});
//# sourceMappingURL=VariableSizeGrid.js.map